/**
 * Ticket reminder hooks (plan plans/2026-08-08-reminders-and-tasks.md
 * A14) - the "Remind Me" row in the agent Properties card.
 *
 * Wraps:
 *   GET    /api/v1/agent/tickets/{n}/reminder -> useTicketReminder
 *   PUT    ...                                -> useSetTicketReminder
 *   DELETE ...                                -> useClearTicketReminder
 *
 * ``ticketNumber`` is the NUMERIC ticket_number, not the public code -
 * the reminder routes declare an int path param (unlike the detail
 * route, which resolves code strings).
 *
 * The GET 404s when no pending reminder exists. That is the normal
 * "nothing set" state, not an error, so the query fn catches it and
 * returns null - the control renders the set-form on null and the
 * pending row otherwise, and TanStack never enters error state for
 * the common case.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";

import api from "@/lib/api";
import type { ReminderRequest, ReminderResponse } from "@/types/api";

export function ticketReminderQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "reminder", ticketNumber] as const;
}

export function useTicketReminder(
  ticketNumber: number,
): UseQueryResult<ReminderResponse | null> {
  return useQuery({
    queryKey: ticketReminderQueryKey(ticketNumber),
    queryFn: async () => {
      try {
        const response = await api.get<ReminderResponse>(
          `/agent/tickets/${ticketNumber}/reminder`,
        );
        return response.data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
  });
}

/** Create-or-replace the pending reminder. The server 422s a slot
 * already past in Eastern time (the control filters those out; the
 * 422 is the race backstop and surfaces as a toast). */
export function useSetTicketReminder(
  ticketNumber: number,
): UseMutationResult<ReminderResponse, Error, ReminderRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: ReminderRequest) => {
      const response = await api.put<ReminderResponse>(
        `/agent/tickets/${ticketNumber}/reminder`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketReminderQueryKey(ticketNumber),
      });
    },
  });
}

/** Clear the pending reminder - the x affordance. */
export function useClearTicketReminder(
  ticketNumber: number,
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/agent/tickets/${ticketNumber}/reminder`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ticketReminderQueryKey(ticketNumber),
      });
    },
  });
}
